import React, { useState, useEffect, useCallback, useMemo } from 'react';
import styled from 'styled-components';
import { FiPlus, FiEye, FiX, FiSearch } from 'react-icons/fi';
import { supabase } from '../../config/supabase';
import { useAuthContext } from '../../hooks/useAuthContext';
import { useLanguage } from '../../context/LanguageContext';
import { format } from 'date-fns';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 32px;
  padding: 24px;
  max-width: 100%;
  overflow-x: hidden;
  
  @media (max-width: 768px) {
    padding: 16px;
    gap: 24px;
  }
`;

const Section = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const SectionTitle = styled.h2`
  font-size: 28px;
  font-weight: 700;
  color: #1f2937;
  margin: 0;
`;

const SummaryCard = styled.div`
  background: white;
  border-radius: 16px;
  padding: 32px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 24px;
`;

const SummaryItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const SummaryLabel = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: #6b7280;
`;

const SummaryValue = styled.div`
  font-size: 32px;
  font-weight: 700;
  color: #1f2937;
`;

const SectionHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const Button = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 20px;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  border: none;
  background: #5efba9;
  color: #1f2937;

  &:hover:not(:disabled) {
    background: #4de89e;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const FilterSelect = styled.select`
  padding: 8px 16px;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  font-size: 14px;
  background: white;
  cursor: pointer;
  transition: all 0.2s;

  &:focus {
    outline: none;
    border-color: #5efba9;
  }
`;

const TableWrapper = styled.div`
  background: white;
  border-radius: 12px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
  
  @media (max-width: 768px) {
    margin: 0 -16px;
    border-radius: 0;
    width: calc(100% + 32px);
  }
`;

const Table = styled.table`
  width: 100%;
  min-width: 600px;
  border-collapse: collapse;
`;

const TableHeader = styled.thead`
  background: #f9fafb;
`;

const TableHeaderRow = styled.tr`
  border-bottom: 1px solid #e5e7eb;
`;

const TableHeaderCell = styled.th`
  padding: 12px 16px;
  text-align: left;
  font-size: 14px;
  font-weight: 600;
  color: #6b7280;
`;

const TableBody = styled.tbody``;

const TableRow = styled.tr`
  border-bottom: 1px solid #f3f4f6;
  transition: background 0.2s;

  &:hover {
    background: #f9fafb;
  }

  &:last-child {
    border-bottom: none;
  }
`;

const TableCell = styled.td`
  padding: 12px 16px;
  font-size: 14px;
  color: #1f2937;
`;

const StatusBadge = styled.span`
  padding: 4px 12px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 600;
  background: ${props => {
    switch(props.status) {
      case 'paid': return '#dcfce7';
      case 'approved': return '#dbeafe';
      case 'pending': return '#fef3c7';
      case 'rejected': return '#fee2e2';
      default: return '#f3f4f6';
    }
  }};
  color: ${props => {
    switch(props.status) {
      case 'paid': return '#166534';
      case 'approved': return '#1e40af';
      case 'pending': return '#92400e';
      case 'rejected': return '#dc2626';
      default: return '#6b7280';
    }
  }};
`;

const IconButton = styled.button`
  width: 32px;
  height: 32px;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  background: white;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s;
  color: #6b7280;

  &:hover {
    background: #5efba9;
    border-color: #5efba9;
    color: white;
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 60px 20px;
  color: #6b7280;
`;

const EmptyText = styled.p`
  font-size: 16px;
  color: #9ca3af;
`;

const SearchContainer = styled.div`
  margin-bottom: 24px;
  position: relative;
`;

const SearchInput = styled.input`
  width: 100%;
  padding: 12px 16px 12px 44px;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  font-size: 14px;
  transition: all 0.2s;

  &:focus {
    outline: none;
    border-color: #5efba9;
    box-shadow: 0 0 0 3px rgba(94, 251, 169, 0.1);
  }

  &::placeholder {
    color: #9ca3af;
  }
`;

const SearchIcon = styled(FiSearch)`
  position: absolute;
  left: 16px;
  top: 50%;
  transform: translateY(-50%);
  color: #9ca3af;
  font-size: 18px;
  pointer-events: none;
`;

const ClearButton = styled.button`
  position: absolute;
  right: 12px;
  top: 50%;
  transform: translateY(-50%);
  background: none;
  border: none;
  color: #9ca3af;
  cursor: pointer;
  padding: 4px;
  display: flex;
  align-items: center;
  transition: color 0.2s;

  &:hover {
    color: #6b7280;
  }
`;

// Modal Styles
const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const ModalContainer = styled.div`
  background: white;
  border-radius: 16px;
  width: 90%;
  max-width: 600px;
  max-height: 90vh;
  overflow-y: auto;
  padding: 32px;
  position: relative;
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
`;

const ModalTitle = styled.h2`
  font-size: 24px;
  font-weight: 700;
  color: #1f2937;
  margin: 0;
`;

const CloseButton = styled.button`
  width: 32px;
  height: 32px;
  border: none;
  background: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #6b7280;
  border-radius: 6px;

  &:hover {
    background: #f3f4f6;
  }
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const Label = styled.label`
  font-size: 14px;
  font-weight: 600;
  color: #374151;
`;

const Input = styled.input`
  padding: 10px 16px;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  font-size: 14px;
  transition: all 0.2s;

  &:focus {
    outline: none;
    border-color: #5efba9;
  }
`;

const TextArea = styled.textarea`
  padding: 10px 16px;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  font-size: 14px;
  min-height: 100px;
  resize: vertical;
  transition: all 0.2s;

  &:focus {
    outline: none;
    border-color: #5efba9;
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 12px;
  justify-content: flex-end;
  margin-top: 8px;
`;

const CancelButton = styled.button`
  padding: 10px 20px;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  border: 1px solid #e5e7eb;
  background: white;
  color: #6b7280;

  &:hover {
    background: #f9fafb;
  }
`;

function AdminPayments() {
  const { session } = useAuthContext();
  const { isArabic } = useLanguage();
  const [payableData, setPayableData] = useState({
    totalSold: 0,
    totalPaid: 0,
    totalPending: 0,
    totalRejected: 0,
    remainingPayable: 0
  });
  const [paymentRequests, setPaymentRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState('all');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [brandId, setBrandId] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  
  const [formData, setFormData] = useState({
    amount: '',
    currency: 'OMR',
    period_from: '',
    period_to: '',
    notes: ''
  });

  useEffect(() => {
    if (session) {
      fetchBrandData();
      fetchPaymentRequests();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterStatus, session]);

  const fetchBrandData = useCallback(async () => {
    if (!session) return;
    
    try {
      console.log('📊 Fetching brand payment data for user:', session.user.id);
      
      // Get the user's brand_id from brand_managers
      const { data: brandManager, error: brandError } = await supabase
        .from('brand_managers')
        .select('brand_id')
        .eq('profile_id', session.user.id)
        .single();

      console.log('Brand manager data:', brandManager, 'error:', brandError);

      if (brandError || !brandManager) {
        console.error('Error fetching brand manager:', brandError);
        
        // Fallback: Try to find brand by created_by
        const { data: userBrand } = await supabase
          .from('brands')
          .select('id')
          .eq('created_by', session.user.id)
          .limit(1)
          .single();
        
        if (userBrand) {
          console.log('Found brand by created_by:', userBrand.id);
          setBrandId(userBrand.id);
        } else {
          console.error('No brand found for this user');
        return;
        }
      } else {
        setBrandId(brandManager.brand_id);
      }

      const currentBrandId = brandManager?.brand_id || (await supabase.from('brands').select('id').eq('created_by', session.user.id).limit(1).single()).data?.id;
      
      console.log('Current brand ID:', currentBrandId);

      // ALWAYS calculate manually for accuracy
      console.log('💰 Calculating totals manually...');
      
      // Step 1: Get ALL order items for this brand (regardless of order status for now)
      const { data: allOrderItems, error: allItemsError } = await supabase
        .from('order_items')
        .select('order_id, line_total, shipment_status_name, order:orders(id, status)')
        .eq('brand_id', currentBrandId);

      console.log('All order items for brand:', allOrderItems?.length || 0, allOrderItems);

      let totalSold = 0;
      if (!allItemsError && allOrderItems) {
        // Filter to include items from:
        // 1. Orders with status: completed, confirmed, or processing
        // 2. OR items with delivered shipment status
        const validItems = allOrderItems.filter(item => {
          const orderStatus = item.order?.status;
          const shipmentStatus = (item.shipment_status_name || '').toLowerCase();
          const isDelivered = shipmentStatus.includes('delivered') || shipmentStatus.includes('تم التسليم');
          
          return isDelivered || orderStatus === 'completed' || orderStatus === 'confirmed' || orderStatus === 'processing';
        });
        
        console.log('Valid order items (completed/confirmed/processing/delivered):', validItems.length);
        totalSold = validItems.reduce((sum, item) => sum + parseFloat(item.line_total || 0), 0);
        console.log('Total sold calculated:', totalSold);
      }

      // Calculate totals by status from brand_payment_requests
      const { data: requests, error: requestsError } = await supabase
        .from('brand_payment_requests')
        .select('amount, status')
        .eq('brand_id', currentBrandId);

      console.log('Payment requests:', requests?.length || 0, requests);

      let totalPaid = 0;
      let totalPending = 0;
      let totalRejected = 0;
      
      if (!requestsError && requests) {
        totalPaid = requests
          .filter(req => req.status === 'paid')
          .reduce((sum, req) => sum + parseFloat(req.amount || 0), 0);
        
        totalPending = requests
          .filter(req => req.status === 'pending' || req.status === 'approved')
          .reduce((sum, req) => sum + parseFloat(req.amount || 0), 0);
        
        totalRejected = requests
          .filter(req => req.status === 'rejected')
          .reduce((sum, req) => sum + parseFloat(req.amount || 0), 0);
        
        console.log('Totals by status:', { totalPaid, totalPending, totalRejected });
      }

      // Remaining = Total Sold - (Paid + Pending)
      const remainingPayable = totalSold - (totalPaid + totalPending);

      console.log('✅ Final calculated values:', { 
        totalSold, 
        totalPaid,
        totalPending,
        totalRejected,
        remainingPayable: Math.max(0, remainingPayable),
        brandId: currentBrandId 
      });

      setPayableData({
        totalSold,
        totalPaid,
        totalPending,
        totalRejected,
        remainingPayable: Math.max(0, remainingPayable)
      });
    } catch (error) {
      console.error('❌ Error fetching brand data:', error);
      // Set defaults on error
      setPayableData({
        totalSold: 0,
        totalPaid: 0,
        totalPending: 0,
        totalRejected: 0,
        remainingPayable: 0
      });
    }
  }, [session]);

  const fetchPaymentRequests = useCallback(async () => {
    if (!session) return;
    
    try {
      setLoading(true);
      console.log('📋 Admin: Fetching payment requests for user:', session.user.id);
      
      // Get the user's brand_id
      const { data: brandManager, error: brandError } = await supabase
        .from('brand_managers')
        .select('brand_id')
        .eq('profile_id', session.user.id)
        .single();

      console.log('Brand manager lookup:', { brandManager, brandError });

      if (brandError || !brandManager) {
        console.error('❌ Error fetching brand manager:', brandError);
        
        // Fallback: Check if user created a brand
        const { data: userBrand } = await supabase
          .from('brands')
          .select('id')
          .eq('created_by', session.user.id)
          .limit(1)
          .single();
        
        if (userBrand) {
          console.log('Found brand by created_by:', userBrand.id);
          // Use this brand_id
          const { data, error } = await supabase
            .from('brand_payment_requests')
            .select('*')
            .eq('brand_id', userBrand.id)
            .order('created_at', { ascending: false });
          
          console.log('Payment requests (via created_by):', { count: data?.length || 0, data, error });
          
          if (error) {
            console.error('Error details:', error);
          }
          
          setPaymentRequests(data || []);
          setLoading(false);
          return;
        }
        
        setPaymentRequests([]);
        setLoading(false);
        return;
      }

      console.log('Fetching requests for brand_id:', brandManager.brand_id);

      // Fetch payment requests for this brand
      let query = supabase
        .from('brand_payment_requests')
        .select('*')
        .eq('brand_id', brandManager.brand_id)
        .order('created_at', { ascending: false });

      // Apply filter if not 'all'
      if (filterStatus !== 'all') {
        query = query.eq('status', filterStatus);
      }

      const { data, error } = await query;

      console.log('Payment requests result:', {
        count: data?.length || 0,
        data: data,
        error: error
      });

      if (error) {
        console.error('❌ Error fetching payment requests:', error);
        console.error('Error code:', error.code);
        console.error('Error message:', error.message);
        console.error('Error details:', error.details);
        setPaymentRequests([]);
      } else {
        console.log('✅ Payment requests fetched successfully:', data?.length || 0);
        if (data && data.length > 0) {
          console.log('Sample request:', data[0]);
        }
        setPaymentRequests(data || []);
      }
    } catch (error) {
      console.error('Error fetching payment requests:', error);
      setPaymentRequests([]);
    } finally {
      setLoading(false);
    }
  }, [filterStatus, session]);

  const handleCreateRequest = () => {
    setSelectedRequest(null);
    setFormData({
      amount: '',
      currency: 'OMR',
      period_from: '',
      period_to: '',
      notes: ''
    });
    setIsModalOpen(true);
  };

  const handleViewRequest = (request) => {
    setSelectedRequest(request);
    setFormData({
      amount: request.amount.toString(),
      currency: request.currency || 'OMR',
      period_from: request.period_from || '',
      period_to: request.period_to || '',
      notes: request.notes || ''
    });
    setIsModalOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!session || !brandId) return;

    setSubmitting(true);
    try {
      const requestedAmount = parseFloat(formData.amount);
      
      // When editing, add back the original request amount to get the true available amount
      const originalAmount = selectedRequest ? parseFloat(selectedRequest.amount || 0) : 0;
      const availableToRequest = payableData.remainingPayable + originalAmount;
      
      // Validation: Ensure requested amount doesn't exceed available amount
      if (requestedAmount > availableToRequest) {
        alert(`Request amount (OMR ${requestedAmount.toFixed(2)}) cannot exceed available amount (OMR ${availableToRequest.toFixed(2)}).`);
        setSubmitting(false);
        return;
      }

      // Validation: Ensure requested amount doesn't exceed total sold
      if (requestedAmount > payableData.totalSold) {
        alert(`Request amount (OMR ${requestedAmount.toFixed(2)}) cannot exceed total sold (OMR ${payableData.totalSold.toFixed(2)}).`);
        setSubmitting(false);
        return;
      }

      const requestData = {
        brand_id: brandId,
        requested_by: session.user.id,
        amount: requestedAmount,
        currency: formData.currency,
        period_from: formData.period_from || null,
        period_to: formData.period_to || null,
        notes: formData.notes || null,
        status: 'pending'
      };

      console.log('💳 Creating/updating payment request:', requestData);

      if (selectedRequest) {
        // Update existing request (only if pending)
        if (selectedRequest.status !== 'pending') {
          alert('Only pending requests can be updated.');
          setSubmitting(false);
          return;
        }

        const { data: updatedData, error } = await supabase
          .from('brand_payment_requests')
          .update(requestData)
          .eq('id', selectedRequest.id)
          .select();

        console.log('Update result:', { updatedData, error });

        if (error) throw error;
        alert('Payment request updated successfully!');
      } else {
        // Create new request
        const { data: insertedData, error } = await supabase
          .from('brand_payment_requests')
          .insert([requestData])
          .select();

        console.log('Insert result:', { insertedData, error });

        if (error) {
          console.error('❌ Insert error:', error);
          console.error('Error code:', error.code);
          console.error('Error message:', error.message);
          throw error;
        }
        
        console.log('✅ Payment request created:', insertedData);
        alert('Payment request created successfully!');
      }

      setIsModalOpen(false);
      fetchPaymentRequests();
      fetchBrandData();
    } catch (error) {
      console.error('Error saving payment request:', error);
      alert(`Failed to save payment request: ${error.message}`);
    } finally {
      setSubmitting(false);
    }
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedRequest(null);
    setFormData({
      amount: '',
      currency: 'OMR',
      period_from: '',
      period_to: '',
      notes: ''
    });
  };

  // Filter payment requests by search term
  const filteredPaymentRequests = useMemo(() => {
    if (!searchTerm.trim()) {
      return paymentRequests;
    }
    const term = searchTerm.toLowerCase();
    return paymentRequests.filter(request => {
      const requestId = request.id?.toString() || '';
      const amount = request.amount?.toString() || '';
      const status = (request.status || '').toLowerCase();
      const periodFrom = request.period_from || '';
      const periodTo = request.period_to || '';
      return requestId.includes(term) || amount.includes(term) || status.includes(term) || 
             periodFrom.toLowerCase().includes(term) || periodTo.toLowerCase().includes(term);
    });
  }, [paymentRequests, searchTerm]);

  return (
    <Container>
      <Section>
        <SectionTitle>{isArabic ? 'ملخص المدفوعات' : 'Payment Summary'}</SectionTitle>
        <SummaryCard>
          <SummaryItem>
            <SummaryLabel>{isArabic ? 'إجمالي المبيعات' : 'Total Sold'}</SummaryLabel>
            <SummaryValue>OMR {payableData.totalSold.toFixed(2)}</SummaryValue>
          </SummaryItem>
          <SummaryItem>
            <SummaryLabel>{isArabic ? 'المدفوع' : 'Paid'}</SummaryLabel>
            <SummaryValue style={{ color: '#10b981' }}>OMR {payableData.totalPaid.toFixed(2)}</SummaryValue>
          </SummaryItem>
          <SummaryItem>
            <SummaryLabel>{isArabic ? 'الطلبات المعلقة' : 'Pending Requests'}</SummaryLabel>
            <SummaryValue style={{ color: '#f59e0b' }}>OMR {payableData.totalPending.toFixed(2)}</SummaryValue>
          </SummaryItem>
          <SummaryItem>
            <SummaryLabel>{isArabic ? 'المرفوض' : 'Rejected'}</SummaryLabel>
            <SummaryValue style={{ color: '#ef4444' }}>OMR {payableData.totalRejected.toFixed(2)}</SummaryValue>
          </SummaryItem>
          <SummaryItem>
            <SummaryLabel>{isArabic ? 'المتاح للطلب' : 'Available to Request'}</SummaryLabel>
            <SummaryValue style={{ color: '#5efba9' }}>
              OMR {payableData.remainingPayable.toFixed(2)}
            </SummaryValue>
          </SummaryItem>
        </SummaryCard>
      </Section>

      <Section>
        <SectionHeader>
          <SectionTitle>{isArabic ? 'طلبات الدفع' : 'Payment Requests'}</SectionTitle>
          <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
            <FilterSelect 
              value={filterStatus} 
              onChange={(e) => setFilterStatus(e.target.value)}
            >
              <option value="all">{isArabic ? 'جميع الحالات' : 'All Status'}</option>
              <option value="pending">{isArabic ? 'معلق' : 'Pending'}</option>
              <option value="approved">{isArabic ? 'موافق عليه' : 'Approved'}</option>
              <option value="rejected">{isArabic ? 'مرفوض' : 'Rejected'}</option>
              <option value="paid">{isArabic ? 'مدفوع' : 'Paid'}</option>
            </FilterSelect>
            <Button onClick={handleCreateRequest}>
              <FiPlus /> {isArabic ? 'طلب جديد' : 'New Request'}
            </Button>
          </div>
        </SectionHeader>

        <SearchContainer>
          <SearchIcon />
          <SearchInput
            type="text"
            placeholder={isArabic ? 'ابحث في طلبات الدفع...' : 'Search payment requests...'}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          {searchTerm && (
            <ClearButton onClick={() => setSearchTerm('')} title={isArabic ? 'مسح البحث' : 'Clear search'}>
              <FiX size={18} />
            </ClearButton>
          )}
        </SearchContainer>

        <TableWrapper>
          <Table>
            <TableHeader>
              <TableHeaderRow>
                <TableHeaderCell>{isArabic ? 'معرف الطلب' : 'Request ID'}</TableHeaderCell>
                <TableHeaderCell>{isArabic ? 'المبلغ' : 'Amount'}</TableHeaderCell>
                <TableHeaderCell>{isArabic ? 'الفترة' : 'Period'}</TableHeaderCell>
                <TableHeaderCell>{isArabic ? 'الحالة' : 'Status'}</TableHeaderCell>
                <TableHeaderCell>{isArabic ? 'تاريخ الطلب' : 'Requested Date'}</TableHeaderCell>
                <TableHeaderCell>{isArabic ? 'الإجراءات' : 'Actions'}</TableHeaderCell>
              </TableHeaderRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan="6" style={{ textAlign: 'center', padding: '40px' }}>
                    <EmptyText>{isArabic ? 'جاري التحميل...' : 'Loading...'}</EmptyText>
                  </TableCell>
                </TableRow>
              ) : filteredPaymentRequests.length === 0 ? (
                <TableRow>
                  <TableCell colSpan="6" style={{ textAlign: 'center', padding: '60px 20px' }}>
                    <EmptyState>
                      <EmptyText>{searchTerm ? (isArabic ? 'لم يتم العثور على طلبات دفع تطابق البحث' : 'No payment requests found matching your search') : (isArabic ? 'لم يتم العثور على طلبات دفع' : 'No payment requests found')}</EmptyText>
                    </EmptyState>
                  </TableCell>
                </TableRow>
              ) : (
                filteredPaymentRequests.map((request) => (
                  <TableRow key={request.id}>
                    <TableCell>
                      <code style={{ fontSize: '12px' }}>
                        {request.id.substring(0, 8)}...
                      </code>
                    </TableCell>
                    <TableCell>
                      <strong>{request.currency} {parseFloat(request.amount).toFixed(2)}</strong>
                    </TableCell>
                    <TableCell>
                      {request.period_from && request.period_to ? (
                        <div style={{ fontSize: '12px' }}>
                          {format(new Date(request.period_from), 'MMM dd')} - {format(new Date(request.period_to), 'MMM dd, yyyy')}
                        </div>
                      ) : (
                        '-'
                      )}
                    </TableCell>
                    <TableCell>
                      <StatusBadge status={request.status}>
                        {request.status === 'pending' ? (isArabic ? 'معلق' : 'pending') :
                         request.status === 'approved' ? (isArabic ? 'موافق عليه' : 'approved') :
                         request.status === 'rejected' ? (isArabic ? 'مرفوض' : 'rejected') :
                         request.status === 'paid' ? (isArabic ? 'مدفوع' : 'paid') : request.status}
                      </StatusBadge>
                    </TableCell>
                    <TableCell>
                      {request.created_at
                        ? format(new Date(request.created_at), 'MMM dd, yyyy')
                        : '-'}
                    </TableCell>
                    <TableCell>
                      <IconButton
                        onClick={() => handleViewRequest(request)}
                        title="View Details"
                      >
                          <FiEye />
                        </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableWrapper>
      </Section>

      {/* Create/View Request Modal */}
      {isModalOpen && (
        <ModalOverlay onClick={handleCloseModal}>
          <ModalContainer onClick={(e) => e.stopPropagation()}>
            <ModalHeader>
              <ModalTitle>
                {selectedRequest ? (isArabic ? 'عرض طلب الدفع' : 'View Payment Request') : (isArabic ? 'طلب دفع جديد' : 'New Payment Request')}
              </ModalTitle>
              <CloseButton onClick={handleCloseModal}>
                <FiX />
              </CloseButton>
            </ModalHeader>

            <Form onSubmit={handleSubmit}>
              <FormGroup>
                <Label>{isArabic ? 'المبلغ *' : 'Amount *'}</Label>
                {(() => {
                  // Calculate available amount (add back original if editing)
                  const originalAmount = selectedRequest ? parseFloat(selectedRequest.amount || 0) : 0;
                  const availableAmount = payableData.remainingPayable + originalAmount;
                  return (
                    <>
                      <Input
                        type="number"
                        step="0.01"
                        min="0"
                        max={availableAmount}
                        value={formData.amount}
                        onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                        required
                        disabled={selectedRequest && selectedRequest.status !== 'pending'}
                      />
                      <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '4px' }}>
                        {isArabic ? 'المتاح للطلب:' : 'Available to request:'} <strong style={{ color: '#5efba9' }}>OMR {availableAmount.toFixed(2)}</strong>
                        {selectedRequest && selectedRequest.status === 'pending' && (
                          <span style={{ fontSize: '11px', color: '#6b7280' }}> ({isArabic ? 'شامل المبلغ الحالي' : 'including current amount'})</span>
                        )}
                        <br/>
                        <span style={{ fontSize: '11px' }}>
                          ({isArabic ? 'إجمالي المبيعات:' : 'Total Sold:'} OMR {payableData.totalSold.toFixed(2)} - {isArabic ? 'المدفوع:' : 'Paid:'} OMR {payableData.totalPaid.toFixed(2)} - {isArabic ? 'المعلق:' : 'Pending:'} OMR {payableData.totalPending.toFixed(2)})
                        </span>
                      </div>
                      {formData.amount && parseFloat(formData.amount) > availableAmount && (
                        <div style={{ fontSize: '12px', color: '#dc2626', marginTop: '4px', padding: '8px', background: '#fee2e2', borderRadius: '4px' }}>
                          ⚠️ {isArabic ? 'المبلغ يتجاوز الرصيد المتاح' : 'Amount exceeds available balance'}
                        </div>
                      )}
                    </>
                  );
                })()}
              </FormGroup>

              <FormGroup>
                <Label>{isArabic ? 'العملة *' : 'Currency *'}</Label>
                <Input
                  type="text"
                  value={formData.currency}
                  onChange={(e) => setFormData({ ...formData, currency: e.target.value })}
                  required
                  disabled={selectedRequest && selectedRequest.status !== 'pending'}
                />
              </FormGroup>

              <FormGroup>
                <Label>{isArabic ? 'الفترة من' : 'Period From'}</Label>
                <Input
                  type="date"
                  value={formData.period_from}
                  onChange={(e) => setFormData({ ...formData, period_from: e.target.value })}
                  disabled={selectedRequest && selectedRequest.status !== 'pending'}
                />
              </FormGroup>

              <FormGroup>
                <Label>{isArabic ? 'الفترة إلى' : 'Period To'}</Label>
                <Input
                  type="date"
                  value={formData.period_to}
                  onChange={(e) => setFormData({ ...formData, period_to: e.target.value })}
                  disabled={selectedRequest && selectedRequest.status !== 'pending'}
                />
              </FormGroup>

              <FormGroup>
                <Label>{isArabic ? 'ملاحظات' : 'Notes'}</Label>
                <TextArea
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  placeholder={isArabic ? 'ملاحظات إضافية حول طلب الدفع هذا...' : 'Additional notes about this payment request...'}
                  disabled={selectedRequest && selectedRequest.status !== 'pending'}
                />
              </FormGroup>

              {selectedRequest && (
                <div style={{ padding: '16px', background: '#f9fafb', borderRadius: '8px' }}>
                  <div style={{ fontSize: '14px', fontWeight: 600, marginBottom: '8px' }}>{isArabic ? 'حالة الطلب' : 'Request Status'}</div>
                  <StatusBadge status={selectedRequest.status}>
                    {selectedRequest.status === 'pending' ? (isArabic ? 'معلق' : 'pending') :
                     selectedRequest.status === 'approved' ? (isArabic ? 'موافق عليه' : 'approved') :
                     selectedRequest.status === 'rejected' ? (isArabic ? 'مرفوض' : 'rejected') :
                     selectedRequest.status === 'paid' ? (isArabic ? 'مدفوع' : 'paid') : selectedRequest.status}
                  </StatusBadge>
                </div>
              )}

              {/* Show receipt image for paid requests */}
              {selectedRequest && selectedRequest.status === 'paid' && selectedRequest.img && (
                <div style={{ padding: '16px', background: '#f0fdf4', borderRadius: '8px', border: '1px solid #86efac' }}>
                  <div style={{ fontSize: '14px', fontWeight: 600, marginBottom: '12px', color: '#166534' }}>
                    {isArabic ? 'إيصال الدفع' : 'Payment Receipt'}
                  </div>
                  <img 
                    src={selectedRequest.img} 
                    alt="Payment Receipt" 
                    style={{ 
                      width: '100%', 
                      maxHeight: '300px', 
                      objectFit: 'contain', 
                      borderRadius: '8px',
                      border: '1px solid #e5e7eb',
                      background: 'white'
                    }} 
                  />
                  <a 
                    href={selectedRequest.img} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    style={{ 
                      display: 'inline-block',
                      marginTop: '8px',
                      fontSize: '12px', 
                      color: '#10b981', 
                      textDecoration: 'none' 
                    }}
                  >
                    {isArabic ? 'فتح في نافذة جديدة ←' : 'Open in new tab →'}
                  </a>
                </div>
              )}

              <ButtonGroup>
                <CancelButton type="button" onClick={handleCloseModal}>
                  {selectedRequest ? (isArabic ? 'إغلاق' : 'Close') : (isArabic ? 'إلغاء' : 'Cancel')}
                </CancelButton>
                {(!selectedRequest || selectedRequest.status === 'pending') && (
                  <Button type="submit" disabled={submitting}>
                    {submitting ? (isArabic ? 'جاري الحفظ...' : 'Saving...') : selectedRequest ? (isArabic ? 'تحديث الطلب' : 'Update Request') : (isArabic ? 'إنشاء طلب' : 'Create Request')}
                  </Button>
                )}
              </ButtonGroup>
            </Form>
          </ModalContainer>
        </ModalOverlay>
      )}
    </Container>
  );
}

export default AdminPayments;
